import { useMemo, useRef, useEffect } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import SenderDetail from '#client/components/MoveMoney/components/SummaryFormComponents/SenderDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import {
  convertCentToUSDText,
  isChaseBank,
  resetCounterpartyOnUrl
} from '#client/components/MoveMoney/utils/common';
import { DATE_FORMAT } from '#client/constants/date';
import { ACH_DEPOSIT_SUCCESS_SURVEY } from '#client/constants/hotjarEvents';
import { COUNTERPARTY_ID } from '#client/constants/modals';
import { FUND_BANK_ACCOUNT } from '#client/constants/nextUpCards';
import { TIME_LINE, TRANSFER_TYPE, WHICH_ACCOUNT_STEP } from '#client/constants/transfer';
import useDepositContext from '#client/hooks/useDepositContext';
import useFormStep from '#client/hooks/useFormStep';
import { useHotJarEvent } from '#client/hooks/useHotJar';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';

import { DialogContentStyled, Wrapper, PrintedBox } from './SuccessForm.styles';

const checkIcon = (
  <SharedAsset
    alt=""
    color="secondary"
    height={50}
    src="/s2/icon/check-circle-material-fill.svg"
    width={50}
  />
);

export default function SuccessForm({ handleClose, handleClosePreviousModal }) {
  const sendHotjarEvent = useHotJarEvent();
  const { isWebView } = WebView;
  const { data, setCurrentFormStep, setData } = useFormStep();
  const { depositData, clearDepositData } = useDepositContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const counterpartyId = searchParams.get(COUNTERPARTY_ID);
  const { removeCard } = useNextUpCardsContext();

  const { selectedFromAccount, amount, toAccount, dateInit, description } = data;

  const fromName = selectedFromAccount?.name ?? '';
  const fromBank = selectedFromAccount?.bank ?? '';
  const isHideEyeBtn = isChaseBank({
    bank: fromBank,
    connectedType: selectedFromAccount?.connectedType
  });
  const date = useMemo(() => dayjs(new Date(dateInit)).format(DATE_FORMAT.MOVE_MONEY), [dateInit]);
  const amountInUSD = convertCentToUSDText(amount);
  const toName = toAccount?.name ?? '';

  const handleCloseModal = () => {
    clearDepositData();
    handleClose();
    handleClosePreviousModal();
  };

  const handleCreateAnotherDepositMoney = () => {
    // remove card from next up
    removeCard(FUND_BANK_ACCOUNT);
    setData({
      amount: 0,
      selectedFromAccount: {},
      fromAccounts: [],
      toAccount: {},
      transferType: TRANSFER_TYPE.STANDARD_ACH.value,
      isResetCounterparties: false,
      payeeInfo: null,
      isDisableBack: false,
      confirmationTitle: '',
      dateInit: '',
      transactionId: null,
      idempotencyKey: null
    });
    clearDepositData();
    setCurrentFormStep(WHICH_ACCOUNT_STEP);
    if (counterpartyId) {
      resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
    }
  };

  const contentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  useEffect(() => {
    // Send Hotjar event to open survey
    sendHotjarEvent(ACH_DEPOSIT_SUCCESS_SURVEY);
  }, [sendHotjarEvent]);

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <PrintedBox ref={contentRef}>
                <Stack
                  alignItems="center"
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  gap="8px"
                  justifyContent="center"
                  paddingBottom={{ xs: '32px', lg: '40px' }}
                >
                  {checkIcon}
                  <Heading
                    align="center"
                    family="secondary"
                    size="3xl"
                    weight="bold"
                  >
                    Your deposit is processing
                  </Heading>
                </Stack>
                <Paper variant="outlined">
                  <TransactionDetail
                    amount={amountInUSD}
                    dateInitiated={date}
                    status="In transit"
                    timeline={TIME_LINE.SAME_DAY_ACH}
                    title="Deposit details"
                    transactionDescription={description}
                    transactionNumber={depositData?.transactionId}
                    transferType="ACH"
                  />
                  <Divider />
                  <RecipientDetail
                    accountHolder={toName}
                    accountNumber={toAccount?.accountNumber}
                    bank="ZenBusiness"
                    fullAccountNumber={toAccount?.accountNumber}
                  />
                  <Divider />
                  <SenderDetail
                    accountHolder={fromName}
                    accountNumber={selectedFromAccount?.accountNumber}
                    bank={fromBank}
                    fullAccountNumber={isHideEyeBtn ? '' : selectedFromAccount?.fullAccountNumber}
                  />
                </Paper>
              </PrintedBox>
              {!isWebView && (
                <Box mt={{ xs: '32px', lg: '40px' }}>
                  <Text size="sm">
                    It is recommended that you print this page{' '}
                    <Text
                      color="blue"
                      component="span"
                      onClick={handlePrint}
                      size="sm"
                      sx={{ cursor: 'pointer' }}
                    >
                      print this page
                    </Text>{' '}
                    for your records.
                  </Text>
                </Box>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            fullWidth
            onClick={handleCreateAnotherDepositMoney}
            size="large"
            variant="outlined"
          >
            Create another
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            size="large"
            variant="contained"
          >
            Close
          </LoadingButton>
        }
        sx={{
          paddingLeft: '16px',
          paddingRight: '16px'
        }}
        type={ACTION_BUTTON_TYPE.TERTIARY}
      />
    </>
  );
}

SuccessForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleClosePreviousModal: PropTypes.func.isRequired
};
